import { Component, computed, input, output, signal, HostListener, effect, ChangeDetectionStrategy } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  Referral,
  AcceptReferralClinicalData,
  Patient,
  Medication,
  Allergy,
  Problem,
  PatientDiagnosisCode
} from '@xmedical/core';
import {
  ClinicalDataMergeSectionComponent,
  MergeDataItem,
  MergeSelectionChangeEvent
} from '../clinical-data-merge-section/clinical-data-merge-section.component';

@Component({
  selector: 'xm-accept-referral-merge',
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    ClinicalDataMergeSectionComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './accept-referral-merge.component.html',
  styleUrl: './accept-referral-merge.component.scss'
})
export class AcceptReferralMergeComponent {
  // Inputs — all data provided by the container/app
  referral = input<Referral | null>(null);
  existingPatient = input<Patient | null>(null);
  existingMedications = input<Medication[]>([]);
  existingAllergies = input<Allergy[]>([]);
  existingProblems = input<Problem[]>([]);
  existingDiagnosis = input<PatientDiagnosisCode[]>([]);
  isLoading = input(false);
  error = input<string | null>(null);
  isSubmitting = input(false);

  // Outputs — events emitted to the container/app
  acceptMerge = output<AcceptReferralClinicalData>();
  goBack = output<void>();

  // Selection state - items to ADD from referral
  medicationsToAdd = signal<Set<number>>(new Set());
  allergiesToAdd = signal<Set<number>>(new Set());
  problemsToAdd = signal<Set<number>>(new Set());
  diagnosisToAdd = signal<Set<string>>(new Set());

  // Selection state - items to REMOVE from existing patient
  medicationsToRemove = signal<Set<number>>(new Set());
  allergiesToRemove = signal<Set<number>>(new Set());
  problemsToRemove = signal<Set<number>>(new Set());
  diagnosisToRemove = signal<Set<string>>(new Set());

  // Scroll state for action bar shadow
  isScrolled = signal(false);

  @HostListener('window:scroll')
  onScroll(): void {
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    const isAtBottom = scrollPosition + windowHeight >= documentHeight - 20;
    this.isScrolled.set(!isAtBottom);
  }

  // Computed: Patient snapshot from referral
  patientSnapshot = computed(() => this.referral()?.referringPatientSnapshot || null);

  // Computed: Patient name
  patientName = computed(() => {
    const p = this.existingPatient();
    if (!p) return 'Unknown Patient';
    return p.fullName || `${p.firstName || ''} ${p.surname || ''}`.trim() || 'Unknown Patient';
  });

  // Computed: Profile ID display
  profileIdDisplay = computed(() => {
    const p = this.existingPatient();
    return p?.profileId || '';
  });

  // Convert existing patient data to MergeDataItem format
  existingMedicationItems = computed<MergeDataItem[]>(() => {
    return this.existingMedications().map(m => ({
      id: m.id,
      primary: m.description,
      secondary: m.dosage ? `Dosage: ${m.dosage}` : undefined
    }));
  });

  existingAllergyItems = computed<MergeDataItem[]>(() => {
    return this.existingAllergies().map(a => ({
      id: a.id,
      primary: a.allergy,
      secondary: a.comment || undefined
    }));
  });

  existingProblemItems = computed<MergeDataItem[]>(() => {
    return this.existingProblems().map(p => ({
      id: p.id,
      primary: p.problem,
      secondary: p.comment || undefined
    }));
  });

  existingDiagnosisItems = computed<MergeDataItem[]>(() => {
    return this.existingDiagnosis().map(d => ({
      id: d.icD10Code,
      primary: d.icD10Code,
      secondary: d.comment || undefined
    }));
  });

  // Convert referral snapshot data to MergeDataItem format
  referralMedicationItems = computed<MergeDataItem[]>(() => {
    const meds = this.patientSnapshot()?.medication || [];
    return meds.map(m => ({
      id: m.id,
      primary: m.description,
      secondary: m.dosage ? `Dosage: ${m.dosage}` : undefined
    }));
  });

  referralAllergyItems = computed<MergeDataItem[]>(() => {
    const allergies = this.patientSnapshot()?.allergies || [];
    return allergies.map(a => ({
      id: a.id,
      primary: a.allergy,
      secondary: a.comment || undefined
    }));
  });

  referralProblemItems = computed<MergeDataItem[]>(() => {
    const problems = this.patientSnapshot()?.problemList || [];
    return problems.map(p => ({
      id: p.id,
      primary: p.problem,
      secondary: p.comment || undefined
    }));
  });

  referralDiagnosisItems = computed<MergeDataItem[]>(() => {
    const diagnosis = this.patientSnapshot()?.diagnosis || [];
    return diagnosis.map(d => ({
      id: d.icD10Code,
      primary: d.icD10Code,
      secondary: d.comment || undefined
    }));
  });

  // Computed: Sets of existing item IDs for duplicate detection
  existingMedicationIds = computed(() => {
    const descriptions = new Set(this.existingMedications().map(m => m.description.toLowerCase().trim()));
    const referralMeds = this.patientSnapshot()?.medication || [];
    return new Set(referralMeds.filter(m => descriptions.has(m.description.toLowerCase().trim())).map(m => m.id));
  });

  existingAllergyIds = computed(() => {
    const names = new Set(this.existingAllergies().map(a => a.allergy.toLowerCase().trim()));
    const referralAllergies = this.patientSnapshot()?.allergies || [];
    return new Set(referralAllergies.filter(a => names.has(a.allergy.toLowerCase().trim())).map(a => a.id));
  });

  existingProblemIds = computed(() => {
    const names = new Set(this.existingProblems().map(p => p.problem.toLowerCase().trim()));
    const referralProblems = this.patientSnapshot()?.problemList || [];
    return new Set(referralProblems.filter(p => names.has(p.problem.toLowerCase().trim())).map(p => p.id));
  });

  existingDiagnosisIds = computed(() => {
    const codes = new Set(this.existingDiagnosis().map(d => d.icD10Code));
    const referralDiagnosis = this.patientSnapshot()?.diagnosis || [];
    return new Set(referralDiagnosis.filter(d => codes.has(d.icD10Code)).map(d => d.icD10Code));
  });

  // Computed: Has any data to show
  hasAnyData = computed(() => {
    return this.existingMedicationItems().length > 0 ||
           this.referralMedicationItems().length > 0 ||
           this.existingAllergyItems().length > 0 ||
           this.referralAllergyItems().length > 0 ||
           this.existingProblemItems().length > 0 ||
           this.referralProblemItems().length > 0 ||
           this.existingDiagnosisItems().length > 0 ||
           this.referralDiagnosisItems().length > 0;
  });

  // Initialize selections when data arrives
  private initEffect = effect(() => {
    // Track all inputs that trigger re-initialization
    const referral = this.referral();
    this.existingMedications();
    this.existingAllergies();
    this.existingProblems();
    this.existingDiagnosis();

    if (!referral || this.isLoading()) return;

    this.initializeSelections();
  });

  // Selection change handlers
  onMedicationSelectionChange(event: MergeSelectionChangeEvent): void {
    if (event.type === 'add') {
      this.medicationsToAdd.update(set => {
        const newSet = new Set(set);
        if (event.selected) { newSet.add(event.id as number); } else { newSet.delete(event.id as number); }
        return newSet;
      });
    } else {
      this.medicationsToRemove.update(set => {
        const newSet = new Set(set);
        if (event.selected) { newSet.add(event.id as number); } else { newSet.delete(event.id as number); }
        return newSet;
      });
    }
  }

  onAllergySelectionChange(event: MergeSelectionChangeEvent): void {
    if (event.type === 'add') {
      this.allergiesToAdd.update(set => {
        const newSet = new Set(set);
        if (event.selected) { newSet.add(event.id as number); } else { newSet.delete(event.id as number); }
        return newSet;
      });
    } else {
      this.allergiesToRemove.update(set => {
        const newSet = new Set(set);
        if (event.selected) { newSet.add(event.id as number); } else { newSet.delete(event.id as number); }
        return newSet;
      });
    }
  }

  onProblemSelectionChange(event: MergeSelectionChangeEvent): void {
    if (event.type === 'add') {
      this.problemsToAdd.update(set => {
        const newSet = new Set(set);
        if (event.selected) { newSet.add(event.id as number); } else { newSet.delete(event.id as number); }
        return newSet;
      });
    } else {
      this.problemsToRemove.update(set => {
        const newSet = new Set(set);
        if (event.selected) { newSet.add(event.id as number); } else { newSet.delete(event.id as number); }
        return newSet;
      });
    }
  }

  onDiagnosisSelectionChange(event: MergeSelectionChangeEvent): void {
    if (event.type === 'add') {
      this.diagnosisToAdd.update(set => {
        const newSet = new Set(set);
        if (event.selected) { newSet.add(event.id as string); } else { newSet.delete(event.id as string); }
        return newSet;
      });
    } else {
      this.diagnosisToRemove.update(set => {
        const newSet = new Set(set);
        if (event.selected) { newSet.add(event.id as string); } else { newSet.delete(event.id as string); }
        return newSet;
      });
    }
  }

  onAcceptAndMerge(): void {
    if (this.isSubmitting()) return;

    const clinicalData: AcceptReferralClinicalData = {
      addMedicationIds: Array.from(this.medicationsToAdd()),
      addProblemIds: Array.from(this.problemsToAdd()),
      addAllergyIds: Array.from(this.allergiesToAdd()),
      addDiagnosisCodes: Array.from(this.diagnosisToAdd()),
      removeMedicationIds: Array.from(this.medicationsToRemove()),
      removeProblemIds: Array.from(this.problemsToRemove()),
      removeAllergyIds: Array.from(this.allergiesToRemove()),
      removeDiagnosisCodes: Array.from(this.diagnosisToRemove())
    };

    this.acceptMerge.emit(clinicalData);
  }

  onGoBack(): void {
    this.goBack.emit();
  }

  private initializeSelections(): void {
    // Pre-select all NEW referral items to add
    const existingMedIds = this.existingMedicationIds();
    const newMedIds = new Set(
      this.referralMedicationItems()
        .filter(m => !existingMedIds.has(m.id as number))
        .map(m => m.id as number)
    );
    this.medicationsToAdd.set(newMedIds);

    const existingAllergyIds = this.existingAllergyIds();
    const newAllergyIds = new Set(
      this.referralAllergyItems()
        .filter(a => !existingAllergyIds.has(a.id as number))
        .map(a => a.id as number)
    );
    this.allergiesToAdd.set(newAllergyIds);

    const existingProblemIds = this.existingProblemIds();
    const newProblemIds = new Set(
      this.referralProblemItems()
        .filter(p => !existingProblemIds.has(p.id as number))
        .map(p => p.id as number)
    );
    this.problemsToAdd.set(newProblemIds);

    const existingDiagIds = this.existingDiagnosisIds();
    const newDiagCodes = new Set(
      this.referralDiagnosisItems()
        .filter(d => !existingDiagIds.has(d.id as string))
        .map(d => d.id as string)
    );
    this.diagnosisToAdd.set(newDiagCodes);

    // Pre-select items for removal (items in patient but not in referral)
    const referralMedNames = new Set(
      this.referralMedicationItems().map(m => m.primary.toLowerCase().trim())
    );
    const medsToRemove = new Set(
      this.existingMedicationItems()
        .filter(m => !referralMedNames.has(m.primary.toLowerCase().trim()))
        .map(m => m.id as number)
    );
    this.medicationsToRemove.set(medsToRemove);

    const referralAllergyNames = new Set(
      this.referralAllergyItems().map(a => a.primary.toLowerCase().trim())
    );
    const allergiesToRemove = new Set(
      this.existingAllergyItems()
        .filter(a => !referralAllergyNames.has(a.primary.toLowerCase().trim()))
        .map(a => a.id as number)
    );
    this.allergiesToRemove.set(allergiesToRemove);

    const referralProblemNames = new Set(
      this.referralProblemItems().map(p => p.primary.toLowerCase().trim())
    );
    const problemsToRemove = new Set(
      this.existingProblemItems()
        .filter(p => !referralProblemNames.has(p.primary.toLowerCase().trim()))
        .map(p => p.id as number)
    );
    this.problemsToRemove.set(problemsToRemove);

    const referralDiagCodes = new Set(
      this.referralDiagnosisItems().map(d => (d.id as string).toLowerCase().trim())
    );
    const diagnosisToRemove = new Set(
      this.existingDiagnosisItems()
        .filter(d => !referralDiagCodes.has((d.id as string).toLowerCase().trim()))
        .map(d => d.id as string)
    );
    this.diagnosisToRemove.set(diagnosisToRemove);
  }
}
